import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import ModrinthService from '../services/ModrinthService';

interface ServerConfigProps {
  serverName: string;
  onClose: () => void;
  onSave: () => void;
}

interface ModDependency {
  mod_id: string;
  required: boolean;
  version_range?: string;
}

interface Mod {
  name: string;
  mod_id: string;
  version: string;
  url?: string;
  enabled: boolean;
  server_side: boolean;
  client_side: boolean;
  source?: 'url' | 'curseforge' | 'modrinth';
  project_id?: string;
  file_id?: string;
  description?: string;
  categories?: string[];
  dependencies?: ModDependency[];
  minecraft_versions?: string[];
  added_date?: string;
}

interface ModLoaderConfig {
  type: 'vanilla' | 'forge' | 'fabric' | 'quilt' | 'neoforge';
  version: string;
  minecraft_version: string;
  mods: Mod[];
  config_overrides: {
    forge_config?: Record<string, any>;
    fabric_config?: Record<string, any>;
    performance_mods?: boolean;
  };
}

interface MinecraftConfig {
  server_properties: {
    difficulty: 'peaceful' | 'easy' | 'normal' | 'hard';
    game_mode: 'survival' | 'creative' | 'adventure' | 'spectator';
    max_players: number;
    view_distance: number;
    simulation_distance: number;
    spawn_protection: number;
    enable_pvp: boolean;
    enable_command_block: boolean;
    allow_nether: boolean;
    allow_flight: boolean;
    enforce_whitelist: boolean;
    whitelist: string[];
    ops: string[];
  };
  world_generation: {
    seed: string;
    world_type: 'default' | 'flat' | 'large_biomes' | 'amplified';
    generate_structures: boolean;
    level_name: string;
  };
  performance: {
    memory: string;
    jvm_opts: string;
    cpu_limit: number;
  };
  docker_config: {
    image: string;
    environment_vars: Record<string, string>;
    ports: Record<string, number>;
  };
  instance_config: {
    instance_type: 't3a.micro' | 't3a.small' | 't3a.medium' | 't3a.large' | 't3a.xlarge' | 't3a.2xlarge';
    use_spot_instance: boolean;
  };
  mod_loader?: ModLoaderConfig;
}

const ServerConfig: React.FC<ServerConfigProps> = ({ serverName, onClose, onSave }) => {
  const [config, setConfig] = useState<MinecraftConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'server' | 'world' | 'performance' | 'instance' | 'docker' | 'mods'>('server');
  
  // Modrinth integration state
  const [modSearchQuery, setModSearchQuery] = useState('');
  const [modSearchResults, setModSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showModSearch, setShowModSearch] = useState(false);

  // Helper function to validate mod loader configuration
  const validateModLoaderConfig = (modLoader: ModLoaderConfig): string[] => {
    const errors: string[] = [];
    
    if (modLoader.type !== 'vanilla') {
      if (!modLoader.minecraft_version) {
        errors.push('Minecraft version is required for mod loaders');
      }
      
      // Version compatibility checks
      const mcVersion = modLoader.minecraft_version;
      const loaderVersion = modLoader.version;
      
      if (modLoader.type === 'neoforge' && mcVersion && mcVersion < '1.20.4') {
        errors.push('NeoForge requires Minecraft 1.20.4 or higher');
      }
      
      if (modLoader.type === 'forge' && loaderVersion) {
        // Basic Forge version validation
        if (!/^\d+\.\d+\.\d+$/.test(loaderVersion)) {
          errors.push('Forge version must be in format: major.minor.patch (e.g., 47.2.0)');
        }
      }
      
      if ((modLoader.type === 'fabric' || modLoader.type === 'quilt') && loaderVersion) {
        // Basic Fabric/Quilt version validation
        if (!/^\d+\.\d+\.\d+/.test(loaderVersion)) {
          errors.push(`${modLoader.type} version must be in format: major.minor.patch (e.g., 0.14.24)`);
        }
      }
    }
    
    return errors;
  };

  // Validate individual mods
  const validateMod = (mod: Mod): string[] => {
    const errors: string[] = [];
    
    if (!mod.name.trim()) {
      errors.push('Mod name is required');
    }
    
    if (!mod.mod_id.trim()) {
      errors.push('Mod ID is required');
    }
    
    if (mod.url && mod.url.trim()) {
      // Basic URL validation
      try {
        const url = new URL(mod.url);
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors.push('Mod URL must use HTTP or HTTPS protocol');
        }
        if (!mod.url.toLowerCase().endsWith('.jar')) {
          errors.push('Mod URL should point to a .jar file');
        }
      } catch {
        errors.push('Invalid mod URL format');
      }
    } else {
      errors.push('Mod download URL is required');
    }
    
    return errors;
  };

  // Validate all mods in the configuration
  const validateAllMods = (mods: Mod[]): string[] => {
    const errors: string[] = [];
    const modIds = new Set<string>();
    
    mods.forEach((mod, index) => {
      // Check for duplicate mod IDs
      if (mod.mod_id && modIds.has(mod.mod_id)) {
        errors.push(`Duplicate mod ID "${mod.mod_id}" at position ${index + 1}`);
      } else if (mod.mod_id) {
        modIds.add(mod.mod_id);
      }
      
      // Validate individual mod
      const modErrors = validateMod(mod);
      modErrors.forEach(error => {
        errors.push(`Mod "${mod.name || 'Unnamed'}" (${index + 1}): ${error}`);
      });
    });
    
    return errors;
  };

  // Get recommended instance type based on mod loader
  const getRecommendedInstanceType = (modLoader?: ModLoaderConfig): string => {
    if (!modLoader || modLoader.type === 'vanilla') {
      return 't3a.large';
    }
    
    const modCount = modLoader.mods.length;
    if (modCount > 50 || (modLoader.type === 'forge' && modCount > 30)) {
      return 't3a.2xlarge';
    } else if (modCount > 20 || modLoader.type === 'forge') {
      return 't3a.xlarge';
    } else {
      return 't3a.large';
    }
  };

  // Handle Modrinth URL input and automatic project resolution
  const handleModrinthUrl = async (url: string, modIndex: number) => {
    if (!config?.mod_loader) return;

    try {
      const projectSlug = ModrinthService.extractProjectSlug(url);
      if (projectSlug) {
        const project = await ModrinthService.getProject(projectSlug);
        if (project) {
          const convertedMod = await ModrinthService.convertToMod(
            project,
            config.mod_loader.minecraft_version,
            config.mod_loader.type
          );
          
          if (convertedMod) {
            const updatedMods = [...config.mod_loader.mods];
            updatedMods[modIndex] = convertedMod;

            // Check if Fabric API is required and not already added
            if ((convertedMod as any).fabric_api_required && 
                (config.mod_loader.type === 'fabric' || config.mod_loader.type === 'quilt')) {
              const hasFabricApi = updatedMods.some(m => 
                m.mod_id === 'fabric-api' || m.project_id === 'P7dR8mSH'
              );
              
              if (!hasFabricApi) {
                const fabricApi = await ModrinthService.getFabricApi(config.mod_loader.minecraft_version);
                if (fabricApi) {
                  updatedMods.push(fabricApi);
                  alert(`${convertedMod.name} requires Fabric API. Fabric API has been automatically added.`);
                }
              }
            }

            setConfig(prev => prev?.mod_loader ? {
              ...prev,
              mod_loader: { ...prev.mod_loader, mods: updatedMods }
            } : prev);
            return;
          }
        }
      }
      
      // If Modrinth parsing fails, treat as regular URL
      const updatedMods = [...config.mod_loader.mods];
      updatedMods[modIndex] = { 
        ...updatedMods[modIndex], 
        url: url,
        source: 'url'
      };
      setConfig(prev => prev?.mod_loader ? {
        ...prev,
        mod_loader: { ...prev.mod_loader, mods: updatedMods }
      } : prev);
    } catch (error) {
      console.error('Error processing Modrinth URL:', error);
      // Fallback to treating as regular URL
      const updatedMods = [...config.mod_loader.mods];
      updatedMods[modIndex] = { 
        ...updatedMods[modIndex], 
        url: url,
        source: 'url'
      };
      setConfig(prev => prev?.mod_loader ? {
        ...prev,
        mod_loader: { ...prev.mod_loader, mods: updatedMods }
      } : prev);
    }
  };

  // Search mods using Modrinth API
  const searchMods = async (query: string) => {
    if (!config?.mod_loader || !query.trim()) {
      setModSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await ModrinthService.searchMods(query, {
        loaders: [config.mod_loader.type],
        versions: [config.mod_loader.minecraft_version],
        limit: 10
      });
      
      setModSearchResults(results?.hits || []);
    } catch (error) {
      console.error('Error searching mods:', error);
      setModSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add mod from search results
  const addModFromSearch = async (searchResult: any) => {
    if (!config?.mod_loader) return;

    try {
      const project = await ModrinthService.getProject(searchResult.project_id);
      if (project) {
        const convertedMod = await ModrinthService.convertToMod(
          project,
          config.mod_loader.minecraft_version,
          config.mod_loader.type
        );
        
        if (convertedMod) {
          // Check if mod already exists
          const existingMod = config.mod_loader.mods.find(m => m.mod_id === convertedMod.mod_id);
          if (existingMod) {
            alert(`${convertedMod.name} is already added to your mod list.`);
            return;
          }

          const modsToAdd = [convertedMod];

          // Check if Fabric API is required and not already added
          if ((convertedMod as any).fabric_api_required && 
              (config.mod_loader.type === 'fabric' || config.mod_loader.type === 'quilt')) {
            const hasFabricApi = config.mod_loader.mods.some(m => 
              m.mod_id === 'fabric-api' || m.project_id === 'P7dR8mSH'
            );
            
            if (!hasFabricApi) {
              const fabricApi = await ModrinthService.getFabricApi(config.mod_loader.minecraft_version);
              if (fabricApi) {
                modsToAdd.unshift(fabricApi); // Add Fabric API first
                alert(`${convertedMod.name} requires Fabric API. Both mods will be added.`);
              }
            }
          }

          setConfig(prev => prev?.mod_loader ? {
            ...prev,
            mod_loader: {
              ...prev.mod_loader,
              mods: [...prev.mod_loader.mods, ...modsToAdd]
            }
          } : prev);
          
          setShowModSearch(false);
          setModSearchQuery('');
          setModSearchResults([]);
        }
      }
    } catch (error) {
      console.error('Error adding mod from search:', error);
      alert('Failed to add mod. Please try again.');
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers/${encodeURIComponent(serverName)}/config`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setConfig(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching config:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;

    // Validate mod loader configuration
    if (config.mod_loader) {
      const validationErrors = validateModLoaderConfig(config.mod_loader);
      if (validationErrors.length > 0) {
        setError(`Configuration errors:\n${validationErrors.join('\n')}`);
        return;
      }
      
      // Validate mods
      const modErrors = validateAllMods(config.mod_loader.mods);
      if (modErrors.length > 0) {
        setError(`Mod validation errors:\n${modErrors.join('\n')}`);
        return;
      }
    }

    // Check if instance type is suitable for mod loader
    const recommendedType = getRecommendedInstanceType(config.mod_loader);
    if (config.mod_loader && config.mod_loader.type !== 'vanilla' && 
        config.instance_config.instance_type < recommendedType) {
      const proceed = window.confirm(
        `Warning: ${config.instance_config.instance_type} may not have enough resources for ${config.mod_loader.type}.\n` +
        `Recommended: ${recommendedType} or larger.\n\n` +
        `Continue anyway?`
      );
      if (!proceed) return;
    }

    try {
      setSaving(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers/${encodeURIComponent(serverName)}/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ minecraft_config: config }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save configuration: ${response.status} ${response.statusText}`);
      }

      setError(null);
      onSave();
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [serverName]);

  const updateConfig = (section: keyof MinecraftConfig, field: string, value: any) => {
    if (!config) return;
    
    setConfig(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [field]: value
      }
    }));
  };

  const addToList = (section: 'server_properties', field: 'whitelist' | 'ops', value: string) => {
    if (!config || !value.trim()) return;
    
    const currentList = config[section][field] as string[];
    if (!currentList.includes(value.trim())) {
      updateConfig(section, field, [...currentList, value.trim()]);
    }
  };

  const removeFromList = (section: 'server_properties', field: 'whitelist' | 'ops', index: number) => {
    if (!config) return;
    
    const currentList = config[section][field] as string[];
    updateConfig(section, field, currentList.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h3 style={{ color: '#d32f2f', margin: '0 0 16px 0' }}>Error</h3>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px' }}>
            <button
              onClick={fetchConfig}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          padding: '24px 24px 0 24px',
          borderBottom: '1px solid #ddd',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1
        }}>
          <h3 style={{ margin: '0 0 16px 0' }}>Configure {serverName}</h3>
          
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { key: 'server', label: 'Server Properties' },
              { key: 'world', label: 'World Generation' },
              { key: 'mods', label: 'Mods & Loaders' },
              { key: 'performance', label: 'Performance' },
              { key: 'instance', label: 'Instance & Storage' },
              { key: 'docker', label: 'Docker & Advanced' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '2px solid #2196F3' : '2px solid transparent',
                  backgroundColor: activeTab === tab.key ? '#e3f2fd' : 'transparent',
                  color: activeTab === tab.key ? '#1976d2' : '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.key ? 'bold' : 'normal'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Server Properties Tab */}
          {activeTab === 'server' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Difficulty
                </label>
                <select
                  value={config.server_properties.difficulty}
                  onChange={(e) => updateConfig('server_properties', 'difficulty', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="peaceful">Peaceful</option>
                  <option value="easy">Easy</option>
                  <option value="normal">Normal</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Game Mode
                </label>
                <select
                  value={config.server_properties.game_mode}
                  onChange={(e) => updateConfig('server_properties', 'game_mode', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="survival">Survival</option>
                  <option value="creative">Creative</option>
                  <option value="adventure">Adventure</option>
                  <option value="spectator">Spectator</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Max Players
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={config.server_properties.max_players}
                  onChange={(e) => updateConfig('server_properties', 'max_players', parseInt(e.target.value) || 20)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  View Distance
                </label>
                <input
                  type="number"
                  min="3"
                  max="32"
                  value={config.server_properties.view_distance}
                  onChange={(e) => updateConfig('server_properties', 'view_distance', parseInt(e.target.value) || 10)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Simulation Distance
                </label>
                <input
                  type="number"
                  min="3"
                  max="32"
                  value={config.server_properties.simulation_distance}
                  onChange={(e) => updateConfig('server_properties', 'simulation_distance', parseInt(e.target.value) || 10)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Spawn Protection
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={config.server_properties.spawn_protection}
                  onChange={(e) => updateConfig('server_properties', 'spawn_protection', parseInt(e.target.value) || 16)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {[
                    { key: 'enable_pvp', label: 'Enable PvP' },
                    { key: 'enable_command_block', label: 'Enable Command Blocks' },
                    { key: 'allow_nether', label: 'Allow Nether' },
                    { key: 'allow_flight', label: 'Allow Flight' },
                    { key: 'enforce_whitelist', label: 'Enforce Whitelist' },
                  ].map(option => (
                    <div key={option.key}>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <input
                          type="checkbox"
                          checked={config.server_properties[option.key as keyof typeof config.server_properties] as boolean}
                          onChange={(e) => updateConfig('server_properties', option.key, e.target.checked)}
                          style={{ marginRight: '8px' }}
                        />
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Whitelist Section */}
              <div style={{ gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '16px 0 8px 0' }}>Player Whitelist (UUIDs)</h4>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter player UUID"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addToList('server_properties', 'whitelist', (e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                      if (input) {
                        addToList('server_properties', 'whitelist', input.value);
                        input.value = '';
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {config.server_properties.whitelist.map((uuid, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        padding: '4px 8px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {uuid}
                      <button
                        onClick={() => removeFromList('server_properties', 'whitelist', index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Operators Section */}
              <div style={{ gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '16px 0 8px 0' }}>Server Operators (UUIDs)</h4>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter operator UUID"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addToList('server_properties', 'ops', (e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                      if (input) {
                        addToList('server_properties', 'ops', input.value);
                        input.value = '';
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {config.server_properties.ops.map((uuid, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: '#fff3e0',
                        color: '#f57c00',
                        padding: '4px 8px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {uuid}
                      <button
                        onClick={() => removeFromList('server_properties', 'ops', index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f57c00',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* World Generation Tab */}
          {activeTab === 'world' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  World Seed
                </label>
                <input
                  type="text"
                  value={config.world_generation.seed}
                  onChange={(e) => updateConfig('world_generation', 'seed', e.target.value)}
                  placeholder="Leave empty for random seed"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Numeric seed or text that will be converted to a number
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  World Type
                </label>
                <select
                  value={config.world_generation.world_type}
                  onChange={(e) => updateConfig('world_generation', 'world_type', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="default">Default</option>
                  <option value="flat">Flat/Superflat</option>
                  <option value="large_biomes">Large Biomes</option>
                  <option value="amplified">Amplified</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Level Name
                </label>
                <input
                  type="text"
                  value={config.world_generation.level_name}
                  onChange={(e) => updateConfig('world_generation', 'level_name', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={config.world_generation.generate_structures}
                    onChange={(e) => updateConfig('world_generation', 'generate_structures', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Generate Structures (villages, dungeons, etc.)
                </label>
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Memory Allocation
                </label>
                <select
                  value={config.performance.memory}
                  onChange={(e) => updateConfig('performance', 'memory', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="2G">2 GB</option>
                  <option value="4G">4 GB</option>
                  <option value="6G">6 GB (Recommended)</option>
                  <option value="8G">8 GB</option>
                  <option value="12G">12 GB</option>
                  <option value="16G">16 GB</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  CPU Limit
                </label>
                <input
                  type="number"
                  min="1"
                  max="8"
                  value={config.performance.cpu_limit}
                  onChange={(e) => updateConfig('performance', 'cpu_limit', parseInt(e.target.value) || 2)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Number of CPU cores to allocate to the server
                </small>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  JVM Options
                </label>
                <input
                  type="text"
                  value={config.performance.jvm_opts}
                  onChange={(e) => updateConfig('performance', 'jvm_opts', e.target.value)}
                  placeholder="-XX:+UseG1GC -XX:+DisableExplicitGC"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Advanced JVM performance options. Leave default unless you know what you're doing.
                </small>
              </div>
            </div>
          )}

          {/* Instance & Storage Tab */}
          {activeTab === 'instance' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Instance Type
                </label>
                <select
                  value={config.instance_config.instance_type}
                  onChange={(e) => updateConfig('instance_config', 'instance_type', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="t3a.micro">t3a.micro (2 vCPU, 1 GB RAM)</option>
                  <option value="t3a.small">t3a.small (2 vCPU, 2 GB RAM)</option>
                  <option value="t3a.medium">t3a.medium (2 vCPU, 4 GB RAM)</option>
                  <option value="t3a.large">t3a.large (2 vCPU, 8 GB RAM)</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Higher instance types provide more memory and CPU performance. 
                  Changes require server restart to take effect.
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Instance Pricing
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={`pricing-${serverName}`}
                      checked={!config.instance_config.use_spot_instance}
                      onChange={() => updateConfig('instance_config', 'use_spot_instance', false)}
                      style={{ marginRight: '8px' }}
                    />
                    <div>
                      <strong>On-Demand (Recommended)</strong>
                      <br />
                      <small style={{ color: '#666' }}>
                        Guaranteed availability, predictable pricing. Best for regular gaming sessions.
                      </small>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={`pricing-${serverName}`}
                      checked={config.instance_config.use_spot_instance}
                      onChange={() => updateConfig('instance_config', 'use_spot_instance', true)}
                      style={{ marginRight: '8px' }}
                    />
                    <div>
                      <strong>Spot Instance (Up to 90% savings)</strong>
                      <br />
                      <small style={{ color: '#666' }}>
                        Lower cost but may be interrupted with 2-minute notice. Good for testing or flexible schedules.
                      </small>
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ 
                  backgroundColor: '#fff3e0', 
                  border: '1px solid #ffa726', 
                  borderRadius: '4px', 
                  padding: '12px', 
                  fontSize: '14px'
                }}>
                  <strong>⚠️ Important:</strong> Instance type and pricing changes require you to manually restart 
                  the server to take effect. Stop and start the server after saving these changes.
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '16px 0 8px 0' }}>Instance Type Recommendations</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}>
                    <strong>t3a.micro</strong>
                    <br />
                    <small>1-3 players, minimal mods</small>
                  </div>
                  <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}>
                    <strong>t3a.small</strong>
                    <br />
                    <small>2-5 players, light mods</small>
                  </div>
                  <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}>
                    <strong>t3a.medium</strong>
                    <br />
                    <small>5-10 players, moderate mods</small>
                  </div>
                  <div style={{ padding: '12px', border: '1px solid #2196F3', borderRadius: '6px', backgroundColor: '#e3f2fd' }}>
                    <strong>t3a.large (Recommended)</strong>
                    <br />
                    <small>10-20 players, heavy mods</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mods & Loaders Tab */}
          {activeTab === 'mods' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Mod Loader
                </label>
                <select
                  value={config.mod_loader?.type || 'vanilla'}
                  onChange={(e) => {
                    const loaderType = e.target.value as ModLoaderConfig['type'];
                    if (loaderType === 'vanilla') {
                      setConfig(prev => prev ? { ...prev, mod_loader: undefined } : prev);
                    } else {
                      setConfig(prev => prev ? {
                        ...prev,
                        mod_loader: {
                          type: loaderType,
                          version: '',
                          minecraft_version: '1.20.1',
                          mods: [],
                          config_overrides: {
                            performance_mods: false
                          }
                        }
                      } : prev);
                    }
                  }}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="vanilla">Vanilla (No Mods)</option>
                  <option value="forge">Forge - Most Popular</option>
                  <option value="fabric">Fabric - Lightweight & Fast</option>
                  <option value="quilt">Quilt - Fabric Fork</option>
                  <option value="neoforge">NeoForge - Forge Fork</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Choose a mod loader. Forge supports most mods, Fabric is lightweight and fast.
                </small>
              </div>

              {config.mod_loader && config.mod_loader.type !== 'vanilla' && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                      Minecraft Version
                    </label>
                    <select
                      value={config.mod_loader.minecraft_version}
                      onChange={(e) => setConfig(prev => prev?.mod_loader ? {
                        ...prev,
                        mod_loader: { ...prev.mod_loader, minecraft_version: e.target.value }
                      } : prev)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="1.20.4">1.20.4 (Latest)</option>
                      <option value="1.20.1">1.20.1 (Stable)</option>
                      <option value="1.19.4">1.19.4</option>
                      <option value="1.19.2">1.19.2</option>
                      <option value="1.18.2">1.18.2</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                      {config.mod_loader.type === 'forge' ? 'Forge' : 
                       config.mod_loader.type === 'fabric' ? 'Fabric Loader' :
                       config.mod_loader.type === 'quilt' ? 'Quilt Loader' : 'NeoForge'} Version
                    </label>
                    <input
                      type="text"
                      value={config.mod_loader.version || ''}
                      onChange={(e) => setConfig(prev => prev?.mod_loader ? {
                        ...prev,
                        mod_loader: { ...prev.mod_loader, version: e.target.value || '' }
                      } : prev)}
                      placeholder={
                        config.mod_loader.type === 'forge' ? 'e.g., 47.2.0 (Latest: ~47.x)' :
                        config.mod_loader.type === 'fabric' ? 'e.g., 0.16.14 (Latest: 0.16.14)' :
                        config.mod_loader.type === 'quilt' ? 'e.g., 0.19.0 (Latest: ~0.25.x)' : 'e.g., 20.4.0 (Latest: ~20.x)'
                      }
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Leave empty for latest compatible version
                      </small>
                      {config.mod_loader.version && (
                        <button
                          type="button"
                          onClick={() => setConfig(prev => prev?.mod_loader ? {
                            ...prev,
                            mod_loader: { ...prev.mod_loader, version: '' }
                          } : prev)}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            fontSize: '10px',
                            cursor: 'pointer'
                          }}
                        >
                          Use Latest
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <input
                        type="checkbox"
                        id="performance-mods"
                        checked={config.mod_loader.config_overrides.performance_mods || false}
                        onChange={(e) => setConfig(prev => prev?.mod_loader ? {
                          ...prev,
                          mod_loader: {
                            ...prev.mod_loader,
                            config_overrides: {
                              ...prev.mod_loader.config_overrides,
                              performance_mods: e.target.checked
                            }
                          }
                        } : prev)}
                      />
                      <label htmlFor="performance-mods" style={{ fontWeight: 'bold' }}>
                        Auto-install Performance Mods
                      </label>
                    </div>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Automatically installs optimization mods (Lithium, Phosphor for Fabric/Quilt; OptiFine alternatives for Forge)
                    </small>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      borderRadius: '6px',
                      marginTop: '16px'
                    }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>⚠️ Important Notes</h4>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li><strong>Server Restart Required:</strong> Changing mod loader requires server restart</li>
                        <li><strong>Performance Impact:</strong> Modded servers require more RAM and CPU</li>
                        <li><strong>Startup Time:</strong> Modded servers take 2-5 minutes to start vs 30 seconds for vanilla</li>
                        <li><strong>Instance Type:</strong> Recommend t3a.large or larger for modded servers</li>
                        <li><strong>Client Compatibility:</strong> Players need matching mod loader and mods installed</li>
                      </ul>
                    </div>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <h4 style={{ margin: '16px 0 8px 0' }}>Recommended Instance Types for Modded Servers</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                      <div style={{ 
                        padding: '12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '6px',
                        backgroundColor: config.mod_loader.type === 'fabric' || config.mod_loader.type === 'quilt' ? '#e8f5e8' : '#f5f5f5'
                      }}>
                        <strong>t3a.large</strong><br />
                        <small>Light modpacks (10-30 mods)</small>
                      </div>
                      <div style={{ 
                        padding: '12px', 
                        border: '1px solid #2196F3', 
                        borderRadius: '6px', 
                        backgroundColor: '#e3f2fd'
                      }}>
                        <strong>t3a.xlarge (Recommended)</strong><br />
                        <small>Medium modpacks (30-60 mods)</small>
                      </div>
                      <div style={{ 
                        padding: '12px', 
                        border: '1px solid #ff9800', 
                        borderRadius: '6px',
                        backgroundColor: '#fff3e0'
                      }}>
                        <strong>t3a.2xlarge</strong><br />
                        <small>Heavy modpacks (60+ mods)</small>
                      </div>
                    </div>
                  </div>

                  {/* Mod Management Section */}
                  {config.mod_loader && (() => {
                    const modLoader = config.mod_loader;
                    return (
                    <div style={{ gridColumn: '1 / -1', marginTop: '24px' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#2196F3' }}>📦 Mod Management</h4>
                      
                      {/* Current Mods List */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <strong>Installed Mods ({modLoader.mods.length})</strong>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => setShowModSearch(!showModSearch)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              🔍 Search Mods
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newMod: Mod = {
                                  name: '',
                                  mod_id: '',
                                  version: '',
                                  url: '',
                                  enabled: true,
                                  server_side: true,
                                  client_side: true,
                                  source: 'url',
                                  added_date: new Date().toISOString()
                                };
                                setConfig(prev => prev?.mod_loader ? {
                                  ...prev,
                                  mod_loader: {
                                    ...prev.mod_loader,
                                    mods: [...prev.mod_loader.mods, newMod]
                                  }
                                } : prev);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              + Add Manual
                            </button>
                          </div>
                        </div>

                        {/* Mod Search Interface */}
                        {showModSearch && (
                          <div style={{ 
                            padding: '16px', 
                            backgroundColor: '#f0f8ff', 
                            border: '1px solid #2196F3', 
                            borderRadius: '6px', 
                            marginBottom: '16px' 
                          }}>
                            <h5 style={{ margin: '0 0 12px 0', color: '#1976d2' }}>Search Modrinth for Mods</h5>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                              <input
                                type="text"
                                value={modSearchQuery}
                                onChange={(e) => setModSearchQuery(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    searchMods(modSearchQuery);
                                  }
                                }}
                                placeholder="Search for mods (e.g., JEI, OptiFine, Lithium)"
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => searchMods(modSearchQuery)}
                                disabled={isSearching}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: isSearching ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isSearching ? 'Searching...' : 'Search'}
                              </button>
                            </div>

                            {/* Search Results */}
                            {modSearchResults.length > 0 && (
                              <div style={{ 
                                maxHeight: '200px', 
                                overflowY: 'auto', 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                backgroundColor: 'white'
                              }}>
                                {modSearchResults.map((result, idx) => (
                                  <div
                                    key={result.project_id}
                                    style={{
                                      padding: '12px',
                                      borderBottom: idx < modSearchResults.length - 1 ? '1px solid #eee' : 'none',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    onClick={() => addModFromSearch(result)}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                          {result.title}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                          {result.description}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#888' }}>
                                          Downloads: {result.downloads.toLocaleString()} | 
                                          Categories: {result.categories.join(', ')}
                                        </div>
                                      </div>
                                      <button
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#4CAF50',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '3px',
                                          fontSize: '11px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Add
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {modSearchQuery && !isSearching && modSearchResults.length === 0 && (
                              <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
                                No mods found. Try different search terms.
                              </div>
                            )}
                          </div>
                        )}

                      {/* Mods List */}
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '6px', 
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        backgroundColor: '#f9f9f9'
                      }}>
                        {modLoader.mods.length === 0 ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No mods added yet. Click "Add Mod" to get started.
                          </div>
                        ) : (
                          modLoader.mods.map((mod, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '12px',
                                borderBottom: index < modLoader.mods.length - 1 ? '1px solid #ddd' : 'none',
                                backgroundColor: mod.enabled ? '#fff' : '#f5f5f5'
                              }}
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'start' }}>
                                <div>
                                  {/* Mod Name */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={mod.enabled}
                                      onChange={(e) => {
                                        const updatedMods = [...modLoader.mods];
                                        updatedMods[index] = { ...mod, enabled: e.target.checked };
                                        setConfig(prev => prev?.mod_loader ? {
                                          ...prev,
                                          mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                        } : prev);
                                      }}
                                    />
                                    <input
                                      type="text"
                                      value={mod.name}
                                      onChange={(e) => {
                                        const updatedMods = [...modLoader.mods];
                                        updatedMods[index] = { ...mod, name: e.target.value };
                                        setConfig(prev => prev?.mod_loader ? {
                                          ...prev,
                                          mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                        } : prev);
                                      }}
                                      placeholder="Mod Name (e.g., JEI, Lithium)"
                                      style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '3px',
                                        fontWeight: 'bold',
                                        flex: 1
                                      }}
                                    />
                                  </div>

                                  {/* Mod Details */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                                    <input
                                      type="text"
                                      value={mod.version}
                                      onChange={(e) => {
                                        const updatedMods = [...modLoader.mods];
                                        updatedMods[index] = { ...mod, version: e.target.value };
                                        setConfig(prev => prev?.mod_loader ? {
                                          ...prev,
                                          mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                        } : prev);
                                      }}
                                      placeholder="Version (e.g., 1.20.1-0.11.2)"
                                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '3px' }}
                                    />
                                    <input
                                      type="text"
                                      value={mod.mod_id}
                                      onChange={(e) => {
                                        const updatedMods = [...modLoader.mods];
                                        updatedMods[index] = { ...mod, mod_id: e.target.value };
                                        setConfig(prev => prev?.mod_loader ? {
                                          ...prev,
                                          mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                        } : prev);
                                      }}
                                      placeholder="Mod ID (e.g., jei, lithium)"
                                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '3px' }}
                                    />
                                  </div>

                                  {/* Download URL */}
                                  <input
                                    type="url"
                                    value={mod.url || ''}
                                    onChange={(e) => handleModrinthUrl(e.target.value, index)}
                                    placeholder="Modrinth URL or direct download URL (e.g., https://modrinth.com/mod/jei)"
                                    style={{
                                      width: '100%',
                                      padding: '4px 8px',
                                      border: '1px solid #ddd',
                                      borderRadius: '3px',
                                      marginTop: '8px',
                                      fontSize: '12px'
                                    }}
                                  />

                                  {/* Mod Options */}
                                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '12px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="checkbox"
                                        checked={mod.server_side}
                                        onChange={(e) => {
                                          const updatedMods = [...modLoader.mods];
                                          updatedMods[index] = { ...mod, server_side: e.target.checked };
                                          setConfig(prev => prev?.mod_loader ? {
                                            ...prev,
                                            mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                          } : prev);
                                        }}
                                      />
                                      Server-side
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="checkbox"
                                        checked={mod.client_side}
                                        onChange={(e) => {
                                          const updatedMods = [...modLoader.mods];
                                          updatedMods[index] = { ...mod, client_side: e.target.checked };
                                          setConfig(prev => prev?.mod_loader ? {
                                            ...prev,
                                            mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                          } : prev);
                                        }}
                                      />
                                      Client-side
                                    </label>
                                  </div>
                                </div>

                                {/* Remove Button */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedMods = modLoader.mods.filter((_, i) => i !== index);
                                    setConfig(prev => prev?.mod_loader ? {
                                      ...prev,
                                      mod_loader: { ...prev.mod_loader, mods: updatedMods }
                                    } : prev);
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Popular Mods Quick Add */}
                    <div style={{ marginTop: '16px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px' }}>Quick Add Popular Mods:</strong>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {/* Fabric API for Fabric/Quilt */}
                        {(modLoader.type === 'fabric' || modLoader.type === 'quilt') && (
                          <button
                            type="button"
                            onClick={async () => {
                              const hasFabricApi = modLoader.mods.find(m => m.mod_id === 'fabric-api' || m.project_id === 'P7dR8mSH');
                              if (hasFabricApi) {
                                alert('Fabric API is already added to your mod list.');
                                return;
                              }

                              const fabricApi = await ModrinthService.getFabricApi(modLoader.minecraft_version);
                              if (fabricApi) {
                                setConfig(prev => prev?.mod_loader ? {
                                  ...prev,
                                  mod_loader: {
                                    ...prev.mod_loader,
                                    mods: [...prev.mod_loader.mods, fabricApi]
                                  }
                                } : prev);
                              } else {
                                alert('Failed to fetch Fabric API. Please try again.');
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#4CAF50',
                              border: '1px solid #45a049',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            + Fabric API (Required)
                          </button>
                        )}
                        {[
                          { name: 'JEI', mod_id: 'jei', url: 'https://edge.forgecdn.net/files/4729/341/jei-1.20.1-forge-15.2.0.27.jar', version: '15.2.0.27', categories: ['utility'] },
                          { name: 'Lithium', mod_id: 'lithium', url: 'https://cdn.modrinth.com/data/gvQqBUqZ/versions/ZSNsJrPI/lithium-fabric-mc1.20.1-0.11.2.jar', version: '0.11.2', categories: ['performance'] },
                          { name: 'Iron Chests', mod_id: 'ironchest', url: 'https://edge.forgecdn.net/files/4654/366/IronChests-1.20.1-14.4.4.jar', version: '14.4.4', categories: ['storage'] }
                        ].map(popularMod => (
                          <button
                            key={popularMod.mod_id}
                            type="button"
                            onClick={() => {
                              // Check if mod already exists
                              const existingMod = modLoader.mods.find(m => m.mod_id === popularMod.mod_id);
                              if (existingMod) {
                                alert(`${popularMod.name} is already added to your mod list.`);
                                return;
                              }

                              const newMod: Mod = {
                                name: popularMod.name,
                                mod_id: popularMod.mod_id,
                                version: popularMod.version,
                                url: popularMod.url,
                                enabled: true,
                                server_side: true,
                                client_side: true,
                                source: 'url',
                                categories: popularMod.categories,
                                added_date: new Date().toISOString()
                              };
                              
                              setConfig(prev => prev?.mod_loader ? {
                                ...prev,
                                mod_loader: {
                                  ...prev.mod_loader,
                                  mods: [...prev.mod_loader.mods, newMod]
                                }
                              } : prev);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #2196F3',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            + {popularMod.name}
                          </button>
                        ))}
                      </div>
                      <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                        Note: Popular mods are pre-configured with working download URLs. Verify compatibility with your mod loader.
                      </small>
                    </div>
                  </div>
                  );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Docker & Advanced Tab */}
          {activeTab === 'docker' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Docker Image
                </label>
                <select
                  value={config.docker_config.image}
                  onChange={(e) => updateConfig('docker_config', 'image', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="itzg/minecraft-server:latest">Vanilla Minecraft (Latest)</option>
                  <option value="itzg/minecraft-server:java17">Vanilla Minecraft (Java 17)</option>
                  <option value="itzg/minecraft-server:java11">Vanilla Minecraft (Java 11)</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Different Minecraft server implementations. Latest is recommended for new worlds.
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Minecraft Port
                </label>
                <input
                  type="number"
                  min="1024"
                  max="65535"
                  value={config.docker_config.ports.minecraft}
                  onChange={(e) => updateConfig('docker_config', 'ports', { ...config.docker_config.ports, minecraft: parseInt(e.target.value) || 25565 })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Port for Minecraft connections (default: 25565)
                </small>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Custom Environment Variables
                </label>
                <div style={{ marginBottom: '8px' }}>
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Add custom Docker environment variables for advanced configuration
                  </small>
                </div>
                {Object.entries(config.docker_config.environment_vars).map(([key, value], index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => {
                        const newVars = { ...config.docker_config.environment_vars };
                        delete newVars[key];
                        newVars[e.target.value] = value;
                        updateConfig('docker_config', 'environment_vars', newVars);
                      }}
                      placeholder="Variable name"
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateConfig('docker_config', 'environment_vars', { ...config.docker_config.environment_vars, [key]: e.target.value })}
                      placeholder="Variable value"
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <button
                      onClick={() => {
                        const newVars = { ...config.docker_config.environment_vars };
                        delete newVars[key];
                        updateConfig('docker_config', 'environment_vars', newVars);
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const key = `CUSTOM_VAR_${Object.keys(config.docker_config.environment_vars).length + 1}`;
                    updateConfig('docker_config', 'environment_vars', { ...config.docker_config.environment_vars, [key]: '' });
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add Environment Variable
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div style={{
          borderTop: '1px solid #ddd',
          padding: '16px 24px',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        {error && (
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '14px',
            maxWidth: '300px'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerConfig;